import { BaseModule } from "./baseModule";
import type {
  Application,
  ApplicationWithDetails,
  CreateApplicationRequest,
  UpdateApplicationStatusRequest,
  VideoMetadataResponse,
} from "./types";

import { API_ENDPOINTS } from "./types";
import { normalizeApplication } from "./normalizers";

/**
 * ApplicationsModule handles all application-related operations
 */
export class ApplicationsModule extends BaseModule {
  /**
   * Get all applications for the current user with enhanced details
   * @returns Promise<ApplicationWithDetails[]> - List of user applications with job and resume details
   */
  async getUserApplications(): Promise<ApplicationWithDetails[]> {
    const applications = await this.get<ApplicationWithDetails[]>(
      API_ENDPOINTS.APPLICATIONS,
    );
    return applications.map(normalizeApplication);
  }

  /**
   * Get a specific application by ID
   * @param id - Application ID
   * @returns Promise<ApplicationWithDetails> - Application details
   */
  async getApplicationDetails(id: string): Promise<ApplicationWithDetails> {
    if (!id) {
      throw new Error("Application ID is required");
    }
    return normalizeApplication(
      await this.get<ApplicationWithDetails>(
        API_ENDPOINTS.APPLICATION_DETAILS(id),
      ),
    );
  }

  /**
   * Create a new job application
   * @param data - Application creation data
   * @returns Promise<Application> - Created application
   */
  async createApplication(
    data: CreateApplicationRequest,
  ): Promise<Application> {
    // Validate required fields
    if (!data.job_id) {
      throw new Error("Job ID is required");
    }
    if (!data.resume_id) {
      throw new Error("Resume ID is required");
    }

    return normalizeApplication(
      await this.post<Application>(API_ENDPOINTS.APPLICATIONS, data),
    );
  }

  /**
   * Update application status (admin only)
   * @param id - Application ID
   * @param data - Status update data
   * @returns Promise<Application> - Updated application
   */
  async updateApplicationStatus(
    id: string,
    data: UpdateApplicationStatusRequest,
  ): Promise<Application> {
    if (!id) {
      throw new Error("Application ID is required");
    }
    if (!data.status) {
      throw new Error("Status is required");
    }

    return normalizeApplication(
      await this.patch<Application>(API_ENDPOINTS.APPLICATION_STATUS(id), data),
    );
  }

  /**
   * Withdraw an application
   * @param id - Application ID
   * @returns Promise<Application> - Updated application
   */
  async withdrawApplication(id: string): Promise<Application> {
    return this.updateApplicationStatus(id, { status: "withdrawn" });
  }

  /**
   * Get video metadata for an application
   * @param applicationId - Application ID
   * @returns Promise<VideoMetadataResponse> - Video metadata including signed URL
   */
  async getApplicationVideo(
    applicationId: string,
  ): Promise<VideoMetadataResponse> {
    if (!applicationId) {
      throw new Error("Application ID is required");
    }
    return this.get<VideoMetadataResponse>(
      API_ENDPOINTS.APPLICATION_VIDEO(applicationId),
    );
  }

  /**
   * Check if an application has a video submission
   * @param applicationId - Application ID
   * @returns Promise<boolean> - True if video exists
   */
  async hasApplicationVideo(applicationId: string): Promise<boolean> {
    try {
      await this.getApplicationVideo(applicationId);
      return true;
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "status" in error &&
        error.status === 404
      ) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Delete video from an application
   * @param applicationId - Application ID
   * @returns Promise<void>
   */
  async deleteApplicationVideo(applicationId: string): Promise<void> {
    if (!applicationId) {
      throw new Error("Application ID is required");
    }
    await this.delete(API_ENDPOINTS.APPLICATION_VIDEO(applicationId));
  }

  /**
   * Download video from an application (admin only)
   * @param applicationId - Application ID
   * @returns Promise<Blob> - Video file blob
   */
  async downloadApplicationVideo(applicationId: string): Promise<Blob> {
    if (!applicationId) {
      throw new Error("Application ID is required");
    }
    const response = await this.httpClient.get(
      API_ENDPOINTS.ADMIN_APPLICATION_VIDEO_DOWNLOAD(applicationId),
      { responseType: "blob" },
    );
    return response.data;
  }

  // ============================================================================
  // Enhanced Application Management (Phase 1)
  // ============================================================================

  /**
   * Advance application to next stage (admin only)
   * @param applicationId - Application ID
   * @param sendEmail - Whether to send email notification to candidate
   * @returns Promise<Application> - Updated application
   */
  async advanceApplicationStage(
    applicationId: string,
    sendEmail: boolean = false,
  ): Promise<Application> {
    if (!applicationId) {
      throw new Error("Application ID is required");
    }
    return normalizeApplication(
      await this.post<Application>(
        `/api/admin/applications/${applicationId}/advance-stage`,
        { send_email: sendEmail },
      ),
    );
  }

  /**
   * Send status email to candidate (admin only)
   * @param applicationId - Application ID
   * @param customMessage - Optional custom message to include
   * @returns Promise<{ message: string }> - Success response
   */
  async sendApplicationEmail(
    applicationId: string,
    customMessage?: string,
  ): Promise<{ message: string }> {
    if (!applicationId) {
      throw new Error("Application ID is required");
    }
    return this.post<{ message: string }>(
      `/api/admin/applications/${applicationId}/send-email`,
      { custom_message: customMessage },
    );
  }

  /**
   * Perform bulk action on multiple applications (admin only)
   * @param applicationIds - Array of application IDs
   * @param action - Action to perform: 'advance_stage', 'send_email', or 'update_status'
   * @param options - Additional options (status for update_status, sendEmail flag)
   * @returns Promise<BulkOperationResult> - Result with success/failure counts
   */
  async bulkApplicationAction(
    applicationIds: string[],
    action: "advance_stage" | "send_email" | "update_status",
    options?: {
      status?: string;
      sendEmail?: boolean;
    },
  ): Promise<{
    success_count: number;
    failed_count: number;
    errors: string[];
  }> {
    if (!applicationIds || applicationIds.length === 0) {
      throw new Error("At least one application ID is required");
    }
    if (!action) {
      throw new Error("Action is required");
    }
    if (action === "update_status" && !options?.status) {
      throw new Error("Status is required for update_status action");
    }

    return this.post("/api/admin/applications/bulk-action", {
      application_ids: applicationIds,
      action,
      status: options?.status,
      send_email: options?.sendEmail,
    });
  }
}
